import { useFetcher } from 'react-router';
import { useVariantShipping } from '@modules/product/hook/use-variant-shipping';
import type Variant from '@modules/product/type/variant';

import type { CartItem } from '../type/cart-item';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useIsomorphicLayoutEffect } from 'ahooks';
import type { ItemShippingType } from '../context/ItemShippingContext';
import type { ErrorResponse } from '~/modules/root/graphql/responses';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { isShoppingCartMenuOpenState } from '@rocco/ui/website-header/states/ui-shopping-cart-menu';
import { useSetAtom } from 'jotai';

interface FetcherResult {
    addToShoppingCart: {
        LastAddedItems: CartItem[];
    };
}

export const useAddToCart = (isSample: boolean) => {
    const fetcher = useFetcher<FetcherResult | ErrorResponse>();
    const { shipping } = useVariantShipping(isSample);

    const setIsShoppingCartMenuOpen = useSetAtom(isShoppingCartMenuOpenState);
    const { desktop } = useAppDisplayModeContext();

    const addToCart = (
        variant: Variant,
        quantity: number,
        shippingMethod?: ItemShippingType | null,
    ) => {
        shippingMethod = shippingMethod ?? shipping;
        if (!shippingMethod) return;
        fetcher.submit(
            {
                variant: String(variant.ID),
                quantity: String(quantity),
                [shippingMethod.type === 'delivery'
                    ? 'shippingMethod'
                    : 'pickupLocation']: String(shippingMethod.ID),
                shippingProvider: shippingMethod.provider ?? '',
                sample: isSample.toString(),
            },
            {
                method: 'post',
                action: '/remix-api/ecommerce/add-to-shopping-cart',
            },
        );
    };

    useIsomorphicLayoutEffect(() => {
        if (
            fetcherIsDone(fetcher) &&
            fetcher.data &&
            !('error' in fetcher.data)
        ) {
            setIsShoppingCartMenuOpen(true);
            if (desktop) {
                window?.scrollTo?.({
                    top: 0,
                    behavior: 'smooth',
                });
            }
        }
    }, [fetcher, setIsShoppingCartMenuOpen, desktop]);

    return [addToCart, fetcher] as const;
};
